import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ListEnvelope } from '../../../shared/api/genexus-query';
import type { CrudApi, CrudHooks } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import {
  aprovarNotaFiscal,
  createEmployeeNotasFiscaisApi,
  downloadNotaFiscalFile,
  reprovarNotaFiscal,
  solicitarNotaFiscal,
  uploadNotaFiscalFile,
  type NotasFiscaisListExtraParams,
} from './api';
import type {
  EmployeeNotaFiscalDto,
  EmployeeNotaFiscalInput,
  EmployeeNotaFiscalListItem,
} from './types';

interface WorkflowMutations {
  useSolicitar: () => UseMutationResult<EmployeeNotaFiscalDto, Error, number>;
  useAprovar: () => UseMutationResult<EmployeeNotaFiscalDto, Error, number>;
  useReprovar: () => UseMutationResult<
    EmployeeNotaFiscalDto,
    Error,
    { id: number; motivoReprovacao: string }
  >;
  useUploadFile: () => UseMutationResult<EmployeeNotaFiscalDto, Error, { id: number; file: File }>;
  useDownloadFile: () => UseMutationResult<Blob, Error, number>;
}

export function useEmployeeNotasFiscaisHooks(
  employeeId: number,
  extra: NotasFiscaisListExtraParams = {},
): CrudHooks<EmployeeNotaFiscalListItem, EmployeeNotaFiscalDto, EmployeeNotaFiscalInput, number> & {
  api: CrudApi<EmployeeNotaFiscalListItem, EmployeeNotaFiscalDto, EmployeeNotaFiscalInput, number>;
} & WorkflowMutations {
  const nfApi = useMemo(
    () => createEmployeeNotasFiscaisApi(employeeId, extra),
    // Filtros do List entram em queryKey via `useList`; api só precisa do employeeId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employeeId, extra.statusNotaFiscal, extra.clienteCodigo, extra.mes, extra.ano],
  );
  const queryKey = useMemo(() => ['employees', employeeId, 'notas-fiscais'] as const, [employeeId]);
  const qc = useQueryClient();

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey });
  };

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<EmployeeNotaFiscalListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query, extra],
      queryFn: () => nfApi.list(query),
      placeholderData: (previous) => previous,
    });

  const useDetail = (id: number | null | undefined): UseQueryResult<EmployeeNotaFiscalDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', id],
      queryFn: () => nfApi.get(id as number),
      enabled: id !== null && id !== undefined,
    });

  const useCreate = (): UseMutationResult<EmployeeNotaFiscalDto, Error, EmployeeNotaFiscalInput> =>
    useMutation({
      mutationFn: (input) => nfApi.create(input),
      onSuccess: invalidate,
    });

  const useUpdate = (): UseMutationResult<
    EmployeeNotaFiscalDto,
    Error,
    { id: number; input: EmployeeNotaFiscalInput }
  > =>
    useMutation({
      mutationFn: ({ id, input }) => nfApi.update(id, input),
      onSuccess: invalidate,
    });

  const useDelete = (): UseMutationResult<void, Error, number> =>
    useMutation({
      mutationFn: (id) => nfApi.remove(id),
      onSuccess: invalidate,
    });

  const useSolicitar = (): UseMutationResult<EmployeeNotaFiscalDto, Error, number> =>
    useMutation({
      mutationFn: (id) => solicitarNotaFiscal(employeeId, id),
      onSuccess: invalidate,
    });

  const useAprovar = (): UseMutationResult<EmployeeNotaFiscalDto, Error, number> =>
    useMutation({
      mutationFn: (id) => aprovarNotaFiscal(employeeId, id),
      onSuccess: invalidate,
    });

  const useReprovar = (): UseMutationResult<
    EmployeeNotaFiscalDto,
    Error,
    { id: number; motivoReprovacao: string }
  > =>
    useMutation({
      mutationFn: ({ id, motivoReprovacao }) =>
        reprovarNotaFiscal(employeeId, id, motivoReprovacao),
      onSuccess: invalidate,
    });

  const useUploadFile = (): UseMutationResult<
    EmployeeNotaFiscalDto,
    Error,
    { id: number; file: File }
  > =>
    useMutation({
      mutationFn: ({ id, file }) => uploadNotaFiscalFile(employeeId, id, file),
      onSuccess: invalidate,
    });

  const useDownloadFile = (): UseMutationResult<Blob, Error, number> =>
    useMutation({
      mutationFn: (id) => downloadNotaFiscalFile(employeeId, id),
    });

  return {
    api: nfApi,
    useList,
    useDetail,
    useCreate,
    useUpdate,
    useDelete,
    useSolicitar,
    useAprovar,
    useReprovar,
    useUploadFile,
    useDownloadFile,
  };
}
